/**
 * @fileoverview Password input field with a show/hide visibility toggle.
 * Accepts the same props as a native <input>; manages its own visibility
 * state and renders an eye / eye-slash button inside the input.
 *
 * Firefox bug fixed 2026-05-26 (Ruth UAT):
 *   - The toggle button was visible but click had no effect. Cause: Firefox
 *     enforces a "type-lock" on `<input type="password">` elements once the
 *     browser's password manager has filled them, preventing the type
 *     attribute from changing to "text" via JavaScript. Mutating the prop
 *     in place looked successful in React, but Firefox silently reverted.
 *   - Fix: derive a stable `key` from `visible` so the input is destroyed
 *     and re-created on toggle. The new element has no password-manager
 *     state attached, so the type attribute applies normally. Focus + cursor
 *     position are restored after remount via a ref.
 *   - Defensive CSS: SVG inside the toggle button has `pointer-events: none`
 *     and the button has `z-index: 2` so neither the icon nor the input
 *     ever intercepts the click.
 */

import React, { useEffect, useRef, useState } from 'react';

/**
 * Password input with show/hide toggle.
 *
 * Forwards arbitrary input attributes to the underlying <input> via spread.
 * The wrapper element receives `className="password-input-wrapper"`; the
 * input itself receives `className="input"` plus any extra className from
 * the caller. The toggle button is positioned absolutely inside the wrapper
 * and is announced to screen readers with aria-label + aria-pressed.
 *
 * @param {Object} props
 * @param {string} [props.className] - Extra classes for the input
 * @returns {JSX.Element}
 * @example
 * <PasswordInput
 *   id="password"
 *   name="password"
 *   value={password}
 *   onChange={(e) => setPassword(e.target.value)}
 *   aria-invalid={!!error}
 * />
 */
function PasswordInput({ className = '', ...rest }) {
  const [visible, setVisible] = useState(false);
  const inputRef = useRef(null);
  // Remember whether the previous render had focus on this input so we can
  // restore focus + cursor position after the re-mount triggered by `key`.
  const wasFocusedRef = useRef(false);
  const cursorRef = useRef(null);

  /**
   * Toggle the password visibility. Snapshots focus + cursor state so the
   * post-remount effect can restore them seamlessly.
   */
  const toggle = () => {
    const el = inputRef.current;
    if (el) {
      wasFocusedRef.current = document.activeElement === el;
      cursorRef.current =
        typeof el.selectionStart === 'number' ? el.selectionStart : null;
    }
    setVisible((v) => !v);
  };

  // After the input re-mounts (because `key` changed with visibility),
  // restore focus + cursor if they were on the input before the toggle.
  useEffect(() => {
    if (!wasFocusedRef.current) return;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    if (cursorRef.current != null) {
      try {
        el.setSelectionRange(cursorRef.current, cursorRef.current);
      } catch (_e) {
        // Some input types disallow setSelectionRange — ignore silently.
      }
    }
  }, [visible]);

  return (
    <div className="password-input-wrapper">
      <input
        ref={inputRef}
        // The key derives from `visible` so React unmounts the old element
        // and creates a fresh one on toggle. This defeats Firefox's
        // password-manager type-lock (see file header).
        key={visible ? 'visible' : 'hidden'}
        {...rest}
        type={visible ? 'text' : 'password'}
        className={`input password-input ${className}`.trim()}
      />
      <button
        type="button"
        className="password-toggle"
        onClick={toggle}
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        tabIndex={0}
      >
        {visible ? (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
            <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
            <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
            <line x1="2" y1="2" x2="22" y2="22" />
          </svg>
        ) : (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
    </div>
  );
}

export default PasswordInput;
